import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';
import { FilterTag } from '@/models/product';

const filterTagUrls = (liftOperatorId: string) => ({
    base: urlBuilder(
        'liftoperators',
        `liftoperators/${liftOperatorId}/filter-tags`
    ),
});

type CreateFilterTagPayload = {
    nameDe: string;
    nameEn: string | null;
};

type UpdateFilterTagPayload = {
    nameDe: string;
    nameEn: string | null;
};

export type FilterTagUsage = {
    count: number;
    productConfigurationIds: string[];
};

export const filterTagsApi = baseApiAuthenticated
    .enhanceEndpoints({ addTagTypes: ['FILTER_TAGS', 'PRODUCT_CONFIGURATION'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getFilterTags: builder.query<
                FilterTag[],
                { liftOperatorId: string }
            >({
                query: ({ liftOperatorId }) =>
                    filterTagUrls(liftOperatorId).base,
                providesTags: ['FILTER_TAGS'],
            }),
            createFilterTag: builder.mutation<
                FilterTag,
                { liftOperatorId: string; payload: CreateFilterTagPayload }
            >({
                query: ({ liftOperatorId, payload }) => ({
                    url: filterTagUrls(liftOperatorId).base,
                    method: 'POST',
                    body: payload,
                }),
                // Creating a tag leaves every existing product configuration untouched. Invalidating them as well
                // refetches the configuration currently open in the editor and discards unsaved edits.
                invalidatesTags: ['FILTER_TAGS'],
            }),
            updateFilterTag: builder.mutation<
                FilterTag,
                {
                    liftOperatorId: string;
                    filterTagId: string;
                    payload: UpdateFilterTagPayload;
                }
            >({
                query: ({ liftOperatorId, filterTagId, payload }) => ({
                    url: `${filterTagUrls(liftOperatorId).base}/${filterTagId}`,
                    method: 'PUT',
                    body: payload,
                }),
                invalidatesTags: ['FILTER_TAGS', 'PRODUCT_CONFIGURATION'],
            }),
            deleteFilterTag: builder.mutation<
                void,
                { liftOperatorId: string; filterTagId: string; force?: boolean }
            >({
                query: ({ liftOperatorId, filterTagId, force }) => ({
                    url: `${
                        filterTagUrls(liftOperatorId).base
                    }/${filterTagId}${force ? '?force=true' : ''}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['FILTER_TAGS', 'PRODUCT_CONFIGURATION'],
            }),
            getFilterTagUsage: builder.query<
                FilterTagUsage,
                { liftOperatorId: string; filterTagId: string }
            >({
                query: ({ liftOperatorId, filterTagId }) =>
                    `${filterTagUrls(liftOperatorId).base}/${filterTagId}/usage`,
            }),
            reorderFilterTags: builder.mutation<
                FilterTag[],
                { liftOperatorId: string; orderedIds: string[] }
            >({
                query: ({ liftOperatorId, orderedIds }) => ({
                    url: `${filterTagUrls(liftOperatorId).base}/sort`,
                    method: 'PUT',
                    body: orderedIds,
                }),
                invalidatesTags: ['FILTER_TAGS', 'PRODUCT_CONFIGURATION'],
            }),
        }),
    });

export const {
    useGetFilterTagsQuery,
    useCreateFilterTagMutation,
    useUpdateFilterTagMutation,
    useDeleteFilterTagMutation,
    useGetFilterTagUsageQuery,
    useReorderFilterTagsMutation,
} = filterTagsApi;
